/**
 * Model / blueprint detail page.
 *
 * Minimal, single-column reading flow. The page is built around
 * one centerpiece: the architecture visualization. Everything else
 * is supporting metadata, presented as a clean key/value list.
 *
 *   ← Models                                       72a4…8e9d ⎘
 *
 *   Bitcoin Hourly Price Forecaster
 *   TensorFlow · Sequential · 8 layers · by Anonymous · May 4
 *
 *   <description>
 *
 *   [ Train on my data → ]
 *
 *   ─── ARCHITECTURE ────────────────────────────────────────
 *      INPUT  ────────────────────────────────────────
 *        01  Conv1D              ▓▓▓▓▓▓▓▓▓ 64 · relu
 *        02  BatchNormalization  ▓
 *        03  LSTM                ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓ 128
 *        04  LSTM                ▓▓▓▓▓▓▓▓▓▓▓▓ 64 · returns sequences
 *        05  Dense               ▓▓▓▓▓ 32 · relu
 *        06  Dropout             ▌  rate 0.2
 *        07  Dense               ▌  1 · linear
 *      OUTPUT ────────────────────────────────────────
 *
 *   ─── SPECS ──────────────────────────────────────────────
 *      Framework   TensorFlow 2.10.0
 *      Sequence    20 steps        Batch  64        Epochs 200
 *      Loss        huber           Optimizer  adamw (lr 5e-4)
 *      ...
 *
 *   ─── TAGS ───────────────────────────────────────────────
 *      #forecasting  #lstm  #crypto
 *
 * Foundation models (any family, predictfm included) swap the
 * architecture viz for a Capabilities panel and add a Source row
 * with the HF repo + license at the bottom.
 *
 * Icon usage is deliberate and sparing: back arrow, copy, primary
 * CTA arrow, external link, and the collapse chevron. Nothing else.
 */

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Button, Tooltip, Avatar,
  Stack, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, Select, MenuItem, InputLabel, TextField,
  Alert, Collapse,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { format } from '../../utils/datetime';
import { useSnackbar } from 'notistack';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { ModelsIcon } from '../global/CustomIcons';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import NeuralNetGraph from './NeuralNetGraph';
import { getBlueprintDetail, clearState } from '../../redux/actions/blueprints/detail';
import {
  fetchRequest as deployFoundationRequest,
  clearState as clearDeployFoundationState,
} from '../../redux/actions/blueprints/deployFoundation';
import {
  fetchRequest as byomDownloadRequest,
} from '../../redux/actions/foundation/byomDownload';

// =============================================================================
// Helpers
// =============================================================================

const safeDate = (v, fmt = 'PPP') => {
  if (!v) return null;
  if (typeof v === 'object' && v.$date) v = v.$date;
  try { return format(new Date(v), fmt); } catch { return null; }
};

const fmtParams = (p) => {
  if (!p) return null;
  if (p >= 1e9) return `${(p / 1e9).toFixed(1)}B`;
  if (p >= 1e6) return `${(p / 1e6).toFixed(0)}M`;
  if (p >= 1e3) return `${(p / 1e3).toFixed(0)}K`;
  return p.toLocaleString();
};

const prettyTask = (t) => {
  const map = { forecast: 'Forecast', embed: 'Embed', impute: 'Impute',
                classify: 'Classify', anomaly: 'Anomaly',
                multivariate: 'Multivariate' };
  return map[t] || (t || '').replace(/^./, (c) => c.toUpperCase());
};

const kindLabel = (kind) => {
  const map = {
    foundation_backbone: 'Foundation model',
    foundation:          'Fine-tuned adapter',
    byom:                'BYOM',
    custom:              'Custom architecture',
  };
  return map[kind] || 'Model';
};

// theme.nodeColors uses lowercase, no-space keys. Returns the
// secondary palette as a brand-aligned fallback for unknown layer
// types so the bar viz never has missing color stops.
const layerColor = (theme, type) => {
  const key = String(type || '').toLowerCase().replace(/\s+/g, '');
  return theme.nodeColors?.[key] || theme.palette.secondary.main;
};

// One-line param summary for a single layer, rendered to the right
// of the bar in the architecture viz. Skips null fields and orders
// by importance: units > activation > dropout/rate > flags.
const layerParamSummary = (layer) => {
  const parts = [];
  const units = layer.units ?? layer.filters;
  if (units != null) parts.push(`${units}${layer.filters != null ? ' filters' : ''}`);
  if (layer.kernel_size != null) parts.push(`k=${layer.kernel_size}`);
  if (layer.pool_size   != null) parts.push(`pool ${layer.pool_size}`);
  if (layer.activation)         parts.push(layer.activation);
  if (layer.rate    != null)    parts.push(`rate ${layer.rate}`);
  if (layer.dropout != null)    parts.push(`dropout ${layer.dropout}`);
  if (layer.return_sequences === true) parts.push('returns sequences');
  return parts.join(' · ');
};

// Width %, for the architecture bar viz. Uses log scale so a 1-unit
// Dropout still gets a visible sliver next to a 256-unit Dense.
const barWidthPct = (layer, maxScale) => {
  const units = layer.units ?? layer.filters;
  if (units != null) return Math.max(4, (Math.log2(units + 1) / maxScale) * 100);
  if (layer.rate != null) return 4;
  return 8;
};


// =============================================================================
// Visual primitives
// =============================================================================

/** Section header: thin label + horizontal rule on either side. */
const SectionHeader = ({ children, right }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 5, mb: 2.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 1.2,
          color: 'text.secondary',
          textTransform: 'uppercase',
        }}
      >
        {children}
      </Typography>
      <Box sx={{ flex: 1, height: 1, bgcolor: theme.palette.divider }} />
      {right && (
        <Typography variant="caption" color="text.secondary"
                    sx={{ fontSize: 11 }}>
          {right}
        </Typography>
      )}
    </Stack>
  );
};

/** A single key/value row in the Specs list. Multi-column friendly. */
const Spec = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.6, alignItems: 'baseline', minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 500,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        minWidth: 110,
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {typeof value === 'string' || typeof value === 'number'
        ? <Typography sx={{ fontSize: 13.5, fontWeight: 500, wordBreak: 'break-word' }}>
            {value}
          </Typography>
        : value}
    </Box>
  </Box>
);


/** Tiny segmented toggle (Graph / List) for the architecture header. */
const ArchViewToggle = ({ value, onChange }) => {
  const theme = useTheme();
  const opts = [{ id: 'graph', label: 'Graph' }, { id: 'list', label: 'List' }];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        p: 0.3,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.text.primary, 0.03),
      }}
    >
      {opts.map((o) => {
        const active = value === o.id;
        return (
          <Box
            key={o.id}
            onClick={() => onChange(o.id)}
            sx={{
              cursor: 'pointer',
              px: 1.25,
              py: 0.35,
              borderRadius: 1.1,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.4,
              color: active ? theme.palette.primary.contrastText : 'text.secondary',
              bgcolor: active ? theme.palette.primary.main : 'transparent',
              transition: 'background-color 140ms ease, color 140ms ease',
              '&:hover': { color: active ? undefined : 'text.primary' },
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
};

// =============================================================================
// Architecture viz - the centerpiece
// =============================================================================

/** A single layer row in the waterfall: index, type label, colored
    bar (width = log of units), trailing param summary. */
const LayerRow = ({ index, layer, maxScale }) => {
  const theme = useTheme();
  const color = layerColor(theme, layer.type);
  const widthPct = barWidthPct(layer, maxScale);
  const params = layerParamSummary(layer);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '32px minmax(90px, 130px) 1fr', sm: '32px 160px 1fr' },
        alignItems: 'center',
        py: 0.5,
        gap: 1.5,
        '&:hover .bar': { opacity: 1 },
      }}
    >
      {/* Index */}
      <Typography
        sx={{
          fontFamily: '"Fira Mono", "JetBrains Mono", "Menlo", monospace',
          fontSize: 10.5,
          color: 'text.disabled',
          textAlign: 'right',
          pr: 0.5,
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </Typography>

      {/* Layer type name */}
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: 'text.primary',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {layer.type || 'Layer'}
      </Typography>

      {/* Bar + trailing params */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <Box
          className="bar"
          sx={{
            height: 22,
            width: `${widthPct}%`,
            minWidth: 8,
            maxWidth: '60%',
            bgcolor: alpha(color, 0.18),
            borderLeft: `2px solid ${color}`,
            borderRadius: '0 3px 3px 0',
            opacity: 0.9,
            transition: 'opacity 150ms ease, background-color 150ms ease',
            flexShrink: 0,
          }}
        />
        {params && (
          <Typography
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {params}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/** Architecture waterfall - vertical stack of LayerRow with INPUT /
    OUTPUT markers above and below. */
const ArchitectureFlow = ({ layers }) => {
  const theme = useTheme();
  if (!Array.isArray(layers) || layers.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
        No layers defined for this model.
      </Typography>
    );
  }
  // Use log scale so wildly different unit counts (1 vs 512) coexist.
  const maxUnits = Math.max(
    1,
    ...layers.map((l) => Math.log2((l.units ?? l.filters ?? 0) + 1)),
  );
  return (
    <Box>
      {/* INPUT marker */}
      <FlowMarker label="INPUT" theme={theme} />

      <Box sx={{ pl: 1, py: 1 }}>
        {layers.map((l, i) => (
          <LayerRow key={i} index={i} layer={l} maxScale={maxUnits} />
        ))}
      </Box>

      {/* OUTPUT marker */}
      <FlowMarker label="OUTPUT" theme={theme} />
    </Box>
  );
};

const FlowMarker = ({ label, theme }) => (
  <Stack direction="row" alignItems="center" spacing={1.25}>
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        color: 'text.disabled',
        letterSpacing: 1,
        minWidth: 50,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
  </Stack>
);

/** Estimator summary - sklearn / xgboost have no layers, just one
    estimator name + a hyperparameter dict. */
const EstimatorView = ({ modelBlueprint }) => {
  const estimator = modelBlueprint.estimator || 'Estimator';
  const hp = modelBlueprint.hyperparameters || {};
  const entries = Object.entries(hp);
  return (
    <Box>
      <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
        {estimator}
      </Typography>
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Default hyperparameters.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            columnGap: 4,
          }}
        >
          {entries.map(([k, v]) => (
            <Spec
              key={k}
              label={k}
              value={typeof v === 'object' ? JSON.stringify(v) : String(v)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/** Foundation capabilities panel - clean KV list of what the
    backbone supports. No layers to draw, so we show the things that
    actually matter: tasks, input shape, output shape, context window,
    forecast horizon, optional self-improvement. */
const CapabilitiesView = ({ fmeta }) => {
  const supports = fmeta.supports || {};
  const tasks = supports.tasks || [];
  const rows = [];

  if (tasks.length > 0) {
    rows.push({
      label: 'Tasks',
      value: tasks.map(prettyTask).join(' · '),
    });
  }
  if (supports.multivariate !== undefined) {
    rows.push({
      label: 'Inputs',
      value: (supports.multivariate ? 'Multivariate' : 'Univariate')
           + (supports.covariates ? ' · accepts covariates' : ''),
    });
  }
  if (supports.probabilistic !== undefined) {
    rows.push({
      label: 'Output',
      value: supports.probabilistic
        ? 'Probabilistic (quantile forecasts)'
        : 'Point estimates',
    });
  }
  if (supports.max_context) {
    rows.push({
      label: 'Context',
      value: `${supports.max_context.toLocaleString()} input steps`,
    });
  }
  if (supports.max_horizon) {
    rows.push({
      label: 'Horizon',
      value: `up to ${supports.max_horizon.toLocaleString()} steps`,
    });
  }
  if (fmeta.lookback) {
    rows.push({
      label: 'Lookback',
      value: `${fmeta.lookback.toLocaleString()} steps`,
    });
  }
  if (supports.ttsi) {
    rows.push({
      label: 'Self-improve',
      value: 'Test-time tuning supported',
    });
  }
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
        Capability metadata not declared for this model.
      </Typography>
    );
  }
  return (
    <Box>
      {rows.map((r) => <Spec key={r.label} label={r.label} value={r.value} />)}
    </Box>
  );
};


// =============================================================================
// Main
// =============================================================================

const BlueprintDetailPage = ({
  getBlueprintDetail, clearState, blueprint, isLoading, error,
  deployFoundation, clearDeployFoundationState, deployState,
  byomDownload, byomDownloadState,
}) => {
  const { blueprintId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [workspaceSelected, setWorkspaceSelected] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [archView, setArchView] = useState('graph'); // 'graph' | 'list'

  // Deploy dialog (foundation models only)
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deployReasonType, setDeployReasonType] = useState('');
  const [deployCustomReason, setDeployCustomReason] = useState('');

  useEffect(() => {
    const workspaceId = localStorage.getItem('WORKSPACE_ID');
    if (!workspaceId) { setWorkspaceSelected(false); return; }
    if (blueprintId) getBlueprintDetail(blueprintId);
    return () => clearState();
  }, [getBlueprintDetail, clearState, blueprintId]);

  const isFoundation = blueprint?.kind === 'foundation_backbone';
  const isByom = blueprint?.kind === 'byom';
  const fmeta = blueprint?.foundation || {};

  const fineTuneHref = (isFoundation && blueprint?.uid)
    ? `/pipelines/create?foundation=${encodeURIComponent(blueprint.uid)}`
    : null;

  const deployReasonOptions = [
    { value: 'testing',                 label: 'Testing/Validation' },
    { value: 'performance_improvement', label: 'Performance Improvement' },
    { value: 'accuracy_improvement',    label: 'Accuracy Improvement' },
    { value: 'model_upgrade',           label: 'Model Upgrade' },
    { value: 'scheduled_deployment',    label: 'Scheduled Deployment' },
    { value: 'rollback',                label: 'Rollback to Previous Version' },
    { value: 'baseline_update',         label: 'Baseline Model Update' },
    { value: 'other',                   label: 'Other (specify below)' },
  ];

  const onConfirmDeploy = () => {
    const reason = deployReasonType === 'other'
      ? deployCustomReason
      : (deployReasonOptions.find((o) => o.value === deployReasonType)?.label || '');
    deployFoundation({
      blueprint_uid: blueprint?.uid,
      reason,
    });
  };

  useEffect(() => {
    if (!deployState) return;
    if (deployState.error) {
      enqueueSnackbar(`Deploy failed: ${deployState.error}`, { variant: 'error' });
      clearDeployFoundationState();
      return;
    }
    if (deployState.response && !deployState.isLoading) {
      enqueueSnackbar(
        `Deployed (promotion ${String(deployState.response.promotion_id || '').slice(0, 8)})`,
        { variant: 'success' },
      );
      setDeployDialogOpen(false);
      clearDeployFoundationState();
      navigate('/deployments');
    }
  }, [deployState, enqueueSnackbar, clearDeployFoundationState, navigate]);

  const copyId = (e) => {
    if (e) e.stopPropagation();
    if (!blueprint?.uid) return;
    navigator.clipboard?.writeText(blueprint.uid);
    enqueueSnackbar('Model ID copied', { variant: 'success' });
  };

  // ---------- early returns ----------

  if (!workspaceSelected) {
    return (
      <Box sx={{ p: 6, textAlign: 'center', maxWidth: 480, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom>No workspace selected</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Select a workspace from the dropdown in the header before viewing
          model details.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/workspaces')}>
          Go to workspaces
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !blueprint) {
    return (
      <Box sx={{ p: 6, textAlign: 'center', maxWidth: 480, mx: 'auto' }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error ? 'Could not load model' : 'Model not found'}
        </Typography>
        {error && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {String(error)}
          </Typography>
        )}
        <Button onClick={() => navigate('/models')}>
          Back to models
        </Button>
      </Box>
    );
  }

  // ---------- derived display data ----------

  // Prefer ``name`` (display name) over ``username`` (handle) so users
  // who only filled in their name during signup don't render as
  // "Anonymous" on their own blueprints. Falls through to the
  // foundation vendor for backbones that have no Mongo owner row.
  const ownerName = blueprint.owner_name
                 || blueprint.owner_username
                 || blueprint.owner_details?.name
                 || blueprint.owner_details?.username
                 || (isFoundation ? fmeta.vendor : null)
                 || 'Anonymous';
  const ownerAvatar = blueprint.owner_avatar || blueprint.owner_details?.avatar;
  const addedDate = safeDate(blueprint.created_at, 'MMM d, yyyy')
                 || safeDate(fmeta.installed_at, 'MMM d, yyyy');
  const fullDate = safeDate(blueprint.created_at) || safeDate(fmeta.installed_at);

  const layers = blueprint.model_blueprint?.layers || [];
  const isEstimator = !!blueprint.model_blueprint?.estimator;

  // One-liner subtitle assembled from the strongest available signals.
  const subtitleBits = [];
  subtitleBits.push(kindLabel(blueprint.kind));
  if (!isFoundation && blueprint.metadata?.framework) {
    subtitleBits.push(
      blueprint.metadata.framework
      + (blueprint.metadata.framework_version ? ` ${blueprint.metadata.framework_version}` : ''),
    );
  }
  if (!isFoundation && blueprint.model_blueprint?.model_type) {
    subtitleBits.push(blueprint.model_blueprint.model_type);
  }
  if (!isFoundation && layers.length > 0) {
    subtitleBits.push(`${layers.length} layer${layers.length === 1 ? '' : 's'}`);
  }
  if (isFoundation) {
    const params = fmeta.params || fmeta.parameter_count;
    if (params) subtitleBits.push(fmtParams(params));
    if (fmeta.family) subtitleBits.push(fmeta.family);
  }

  // Specs - the supporting details, rendered as a clean KV grid.
  const specs = [];
  if (!isFoundation) {
    if (blueprint.training_details?.epochs != null) {
      specs.push({ label: 'Epochs', value: blueprint.training_details.epochs });
    }
    if (blueprint.training_details?.batch_size != null) {
      specs.push({ label: 'Batch size', value: blueprint.training_details.batch_size });
    }
    if (blueprint.training_details?.sequence_length != null) {
      specs.push({ label: 'Sequence', value: `${blueprint.training_details.sequence_length} steps` });
    }
    if (blueprint.compilation_details?.loss) {
      specs.push({ label: 'Loss', value: blueprint.compilation_details.loss });
    }
    if (blueprint.compilation_details?.optimizer?.type) {
      const o = blueprint.compilation_details.optimizer;
      const lr = o.learning_rate != null ? ` lr=${o.learning_rate}` : '';
      const wd = o.weight_decay  != null ? ` wd=${o.weight_decay}`  : '';
      specs.push({ label: 'Optimizer', value: `${o.type}${lr}${wd}` });
    }
    if (Array.isArray(blueprint.compilation_details?.metrics) &&
        blueprint.compilation_details.metrics.length > 0) {
      specs.push({
        label: 'Metrics',
        value: blueprint.compilation_details.metrics.join(' · '),
      });
    }
    if (blueprint.data_preprocessing?.global_scaler) {
      specs.push({
        label: 'Scaler',
        value: `${blueprint.data_preprocessing.global_scaler} (global)`,
      });
    }
    if (blueprint.data_split) {
      const ds = blueprint.data_split;
      const test = ds.test_split, val = ds.validation_split;
      if (test != null && val != null) {
        const train = Math.max(0, 1 - test - val);
        specs.push({
          label: 'Split',
          value: `${(train * 100).toFixed(0)}% train · ${(val * 100).toFixed(0)}% val · ${(test * 100).toFixed(0)}% test`,
        });
      }
    }
  }

  // Callbacks list - shown only if non-trivial. Hidden behind the
  // collapsible "Advanced" section so it doesn't pollute the main
  // reading flow.
  const callbacks = blueprint.training_details?.callbacks || [];

  // Per-type scalers - a dict like { numeric: 'MinMax', timestamp: 'Standard' }
  // that we filter down to non-trivial entries.
  const perTypeScalers = Object.entries(
    blueprint.data_preprocessing?.signal_scalers || {},
  ).filter(([, v]) => v && v !== 'None' && v !== 'none');

  const tags = [
    ...(blueprint.categories || []).map((c) => ({ label: c, primary: true })),
    ...(blueprint.tags       || []).map((t) => ({ label: t })),
  ];

  // Whether we even have anything worth putting in "Advanced".
  const hasAdvanced = callbacks.length > 0
                   || perTypeScalers.length > 0
                   || !!blueprint.metadata?.training_environment
                   || !!blueprint.metadata?.model_version;

  return (
    <Box sx={{ width: '100%' }}>
      {/* ====== Breadcrumb + ID copy ======
          Same PageBreadcrumbs component the Segments / Pipelines /
          other detail pages use, with a subtle monospace ID pill on
          the right rail aligned to the breadcrumb baseline. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 1.5, mb: 3, ml: 1, mr: 1 }}
      >
        <PageBreadcrumbs
          items={[
            { text: 'Models', icon: <ModelsIcon />, link: '/models' },
            { text: blueprint.name || blueprint.uid },
          ]}
          mt={0}
          mb={0}
          ml={0}
        />

        {blueprint.uid && (
          <Tooltip title={`${blueprint.uid} — click to copy`} arrow>
            <Box
              onClick={copyId}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                cursor: 'pointer',
                color: 'text.disabled',
                fontFamily: '"Fira Mono", "JetBrains Mono", "Menlo", monospace',
                fontSize: 11,
                transition: 'color 120ms ease',
                flexShrink: 0,
                '&:hover': { color: 'text.primary' },
              }}
            >
              {blueprint.uid.length > 16
                ? `${blueprint.uid.slice(0, 8)}…${blueprint.uid.slice(-4)}`
                : blueprint.uid}
              <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
            </Box>
          </Tooltip>
        )}
      </Stack>

      {/* ====== Hero ====== */}
      <Box sx={{ mb: 5 }}>
        <Typography
          sx={{
            fontSize: { xs: 28, md: 34 },
            fontWeight: 600,
            lineHeight: 1.15,
            letterSpacing: -0.4,
            mb: 1.25,
          }}
        >
          {blueprint.name}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: 13,
            mb: 2.5,
            display: 'inline-flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.75,
          }}
        >
          {subtitleBits.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>}
              <Box component="span">{b}</Box>
            </React.Fragment>
          ))}
        </Typography>

        {(blueprint.description || (isFoundation && fmeta.vendor)) && (
          <Typography
            sx={{
              fontSize: 15,
              lineHeight: 1.65,
              color: 'text.primary',
              mb: 3,
              maxWidth: 720,
            }}
          >
            {blueprint.description ||
              `${kindLabel(blueprint.kind)} from ${fmeta.vendor || 'unknown vendor'}.`}
          </Typography>
        )}

        {/* Author + Action row */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          {isFoundation ? (
            <Stack direction="row" spacing={1.25}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => fineTuneHref && navigate(fineTuneHref)}
                disabled={!fineTuneHref}
                sx={{ height: 38, fontWeight: 500 }}
              >
                Tune on my data
              </Button>
              <Button
                variant="outlined"
                onClick={() => setDeployDialogOpen(true)}
                sx={{ height: 38, fontWeight: 500 }}
              >
                Deploy directly
              </Button>
            </Stack>
          ) : isByom ? (
            <Stack direction="row" spacing={1.25}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate('/pipelines/create',
                                        { state: { blueprintId: blueprint.uid } })}
                sx={{ height: 38, fontWeight: 500 }}
              >
                Use in a pipeline
              </Button>
              <Button
                variant="outlined"
                startIcon={byomDownloadState?.isLoading
                  ? <CircularProgress size={14} color="inherit" />
                  : <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => byomDownload({ blueprint_id: blueprint.uid })}
                disabled={byomDownloadState?.isLoading}
                sx={{ height: 38, fontWeight: 500 }}
              >
                Download model
              </Button>
            </Stack>
          ) : (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/pipelines/create',
                                      { state: { blueprintId: blueprint.uid } })}
              sx={{ height: 38, fontWeight: 500 }}
            >
              Train on my data
            </Button>
          )}

          {/* Author chip - subtle, no card */}
          <Stack direction="row" spacing={1} alignItems="center"
                 sx={{ color: 'text.secondary' }}>
            <Avatar
              src={ownerAvatar}
              sx={{
                width: 22, height: 22, fontSize: 10, fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
              }}
            >
              {ownerName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" sx={{ fontSize: 12.5 }}>
              {ownerName}
              {addedDate && (
                <Tooltip title={fullDate || ''} arrow placement="top">
                  <Box component="span" sx={{ color: 'text.disabled', ml: 0.75 }}>
                    · {addedDate}
                  </Box>
                </Tooltip>
              )}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* ====== Architecture / Capabilities (the centerpiece) ====== */}
      {(() => {
        // The graph view is only meaningful for layered neural nets
        // (TensorFlow / PyTorch). Foundation backbones and tabular
        // estimators (sklearn / xgboost) have no neuron topology to draw.
        const isNeuralNet = !isFoundation && !isEstimator && layers.length > 0;
        return (
          <>
            <SectionHeader
              right={
                isNeuralNet
                  ? <ArchViewToggle value={archView} onChange={setArchView} />
                  : (!isFoundation && layers.length > 0
                      ? `${layers.length} layer${layers.length === 1 ? '' : 's'}`
                      : undefined)
              }
            >
              {isFoundation ? 'Capabilities' : 'Architecture'}
            </SectionHeader>

            {isFoundation
              ? <CapabilitiesView fmeta={fmeta} />
              : (isEstimator
                  ? <EstimatorView modelBlueprint={blueprint.model_blueprint} />
                  : (archView === 'graph'
                      ? <NeuralNetGraph layers={layers} />
                      : <ArchitectureFlow layers={layers} />))
            }
          </>
        );
      })()}

      {/* ====== Specs ====== */}
      {specs.length > 0 && (
        <>
          <SectionHeader>Specs</SectionHeader>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 4,
            }}
          >
            {specs.map((s) => (
              <Spec key={s.label} label={s.label} value={s.value} />
            ))}
          </Box>
        </>
      )}

      {/* ====== Foundation Source / License ====== */}
      {isFoundation && (fmeta.hf_repo || fmeta.license) && (
        <>
          <SectionHeader>Source</SectionHeader>
          {fmeta.hf_repo && (
            <Spec
              label="HuggingFace"
              value={
                <Box
                  component="a"
                  href={`https://huggingface.co/${fmeta.hf_repo}`}
                  target="_blank" rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.6,
                    color: 'primary.main', textDecoration: 'none',
                    fontFamily: '"Fira Mono", "JetBrains Mono", "Menlo", monospace',
                    fontSize: 12.5, fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {fmeta.hf_repo}
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </Box>
              }
            />
          )}
          {fmeta.license && (
            <Spec
              label="License"
              value={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
                    {fmeta.license}
                  </Typography>
                  {typeof fmeta.license_commercial_ok === 'boolean' && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: fmeta.license_commercial_ok
                          ? theme.palette.success.main
                          : theme.palette.warning?.main || theme.palette.error.main,
                      }}
                    >
                      {fmeta.license_commercial_ok
                        ? '· commercial use ok'
                        : '· commercial use restricted'}
                    </Typography>
                  )}
                </Stack>
              }
            />
          )}
          {fmeta.slug && <Spec label="Slug" value={fmeta.slug} />}
        </>
      )}

      {/* ====== Tags ====== */}
      {tags.length > 0 && (
        <>
          <SectionHeader>Tags</SectionHeader>
          <Stack direction="row" flexWrap="wrap" useFlexGap rowGap={0.75} columnGap={0.75}>
            {tags.map((t, i) => (
              <Box
                key={i}
                sx={{
                  display: 'inline-flex', alignItems: 'center',
                  px: 1, py: 0.4,
                  borderRadius: 1,
                  fontSize: 12,
                  fontWeight: 500,
                  color: t.primary ? theme.palette.primary.main : 'text.secondary',
                  bgcolor: t.primary
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.text.primary, 0.04),
                  textTransform: t.primary ? 'capitalize' : 'none',
                }}
              >
                {t.primary ? t.label : `#${t.label}`}
              </Box>
            ))}
          </Stack>
        </>
      )}

      {/* ====== Advanced (collapsible) ====== */}
      {hasAdvanced && (
        <Box sx={{ mt: 5 }}>
          <Button
            onClick={() => setAdvancedOpen((v) => !v)}
            endIcon={
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 18,
                  transform: advancedOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 200ms ease',
                }}
              />
            }
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: 1,
              px: 0,
              minHeight: 'auto',
              height: 'auto',
              '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
            }}
          >
            {advancedOpen ? 'Hide advanced' : 'Show advanced'}
          </Button>
          <Collapse in={advancedOpen}>
            <Box sx={{ mt: 2 }}>
              {callbacks.length > 0 && (
                <Spec
                  label="Callbacks"
                  value={callbacks.map((cb) => cb.type || 'callback').join(' · ')}
                />
              )}
              {perTypeScalers.length > 0 && (
                <Spec
                  label="Per-type scalers"
                  value={perTypeScalers.map(([k, v]) => `${k}: ${v}`).join(' · ')}
                />
              )}
              {blueprint.metadata?.training_environment && (
                <Spec
                  label="Training env"
                  value={blueprint.metadata.training_environment}
                />
              )}
              {blueprint.metadata?.model_version && (
                <Spec label="Version" value={blueprint.metadata.model_version} />
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* ========== Deploy dialog (foundation) ========== */}
      <Dialog
        open={deployDialogOpen}
        onClose={() => setDeployDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Deploy {blueprint?.name} directly</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            Skips fine-tuning and serves the {fmeta.vendor || 'foundation'} model
            in zero-shot mode. Pick "Tune on my data" instead to fine-tune a LoRA
            adapter on your segment first.
          </Alert>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Reason</InputLabel>
            <Select
              value={deployReasonType}
              label="Reason"
              onChange={(e) => setDeployReasonType(e.target.value)}
            >
              {deployReasonOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {deployReasonType === 'other' && (
            <TextField
              label="Custom reason"
              value={deployCustomReason}
              onChange={(e) => setDeployCustomReason(e.target.value)}
              fullWidth multiline rows={3}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeployDialogOpen(false)}
                  disabled={deployState?.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={deployState?.isLoading
              ? <CircularProgress size={14} color="inherit" />
              : <RocketLaunchIcon />}
            onClick={onConfirmDeploy}
            disabled={deployState?.isLoading}
          >
            {deployState?.isLoading ? 'Deploying…' : 'Deploy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


const mapStateToProps = (state) => ({
  blueprint:    state.blueprint_detail.response,
  isLoading:    state.blueprint_detail.isLoading,
  error:        state.blueprint_detail.error,
  deployState:  state.blueprint_deploy_foundation,
  byomDownloadState: state.foundation_byom_download,
});

const mapDispatchToProps = {
  getBlueprintDetail,
  clearState,
  deployFoundation: deployFoundationRequest,
  clearDeployFoundationState,
  byomDownload: byomDownloadRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(BlueprintDetailPage);
